import React, { useEffect, lazy, Suspense } from "react";
import ReactGA from "react-ga4";
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { optimizeFontLoading } from './utils/performanceOptimization.js';

// Critical components - loaded immediately
import Menu from "./components/Menu-Footer/Menu.js";
import Footer from "./components/Menu-Footer/Footer.js";
import Home from './components/Home/Home.js';

// Lazy load non-critical components
const NotFound = lazy(() => import("./components/NotFound.js"));
const Pricing = lazy(() => import('./components/Pricing/Pricing.js'));
const Bookkeeping = lazy(() => import("./components/Services/Bookkeeping/Bookkeeping.js"));
const CFO = lazy(() => import("./components/Services/CFO/CFO.js"));
const Zoho = lazy(() => import("./components/Services/Zoho/Zoho.js"));
const Tax = lazy(() => import("./components/Services/Tax/Tax.js"));
const USA = lazy(() => import("./components/Services/USA/USA.js"));
const BlogPage = lazy(() => import("./components/Blog/Blog.js"));
const BlogPost = lazy(() => import("./components/Blog/BlogPost.js"));
const Help = lazy(() => import("./components/Help/HelpCenter/HelpCenter.js"));
const SubmitRequest = lazy(() => import("./components/Help/QuerySubmit/SubmitRequest.js"));
const PrivacyPolicy = lazy(() => import("./components/Privacy Policy/Privacy-policy.js"));
const SubscriptionAgreement = lazy(() => import("./components/Privacy Policy/SubcriptionAgreement.js"));
const ZohoEvent = lazy(() => import("./components/Events/ZohoEvent.js"));
const WhyBooksComply = lazy(() => import("./components/WhyBooksComply/WhyBooksComply.js"));
      
// Lazy load Help pages
const GettingStarted = lazy(() => import("./components/Help/GettingStarted/GettingStarted.js"));
const General1 = lazy(() => import("./components/Help/GettingStarted/General1.js"));
const General2 = lazy(() => import("./components/Help/GettingStarted/General2.js"));
const General3 = lazy(() => import("./components/Help/GettingStarted/General3.js"));
const General4 = lazy(() => import("./components/Help/GettingStarted/General4.js"));
const General5 = lazy(() => import("./components/Help/GettingStarted/General5.js"));
const General6 = lazy(() => import("./components/Help/GettingStarted/General6.js"));
const Resources = lazy(() => import("./components/Help/Resources/Resources.js"));
const CompanyType1 = lazy(() => import("./components/Help/Resources/CompanyType1.js"));
const CompanyType2 = lazy(() => import("./components/Help/Resources/CompanyType2.js"));
const State2 = lazy(() => import("./components/Help/Resources/State2.js"));
const Restrictions = lazy(() => import("./components/Help/Resources/Restrictions.js"));
const CompanyName1 = lazy(() => import("./components/Help/Resources/CompanyName1.js"));
const Refund = lazy(() => import("./components/Help/Refund/Refund.js"));
const Refund1 = lazy(() => import("./components/Help/Refund/Refund1.js"));
const Refund2 = lazy(() => import("./components/Help/Refund/Refund2.js"));
const Refund3 = lazy(() => import("./components/Help/Refund/Refund3.js"));
const Refund4 = lazy(() => import("./components/Help/Refund/Refund4.js"));

// Initialize Google Analytics
const MEASUREMENT_ID = "G-YS61J3NWNP";
ReactGA.default.initialize(MEASUREMENT_ID);

// Component to handle scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType === 'PUSH' || pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [pathname, navType]);

  return null;
};

// Component to track page views
const TrackPageViews = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.default.send({ hitType: "pageview", page: location.pathname });
  }, [location]);

  return null;
};

const Layout = ({ children }) => {
  const location = useLocation();
           
  const excludeMenuFooterRoutes = [
    // "/help", "/submitrequest", "/getting-started", "/resources",
    // "/general1", "/general2", "/general3", "/companytype1",
    // "/companytype2", "/state2", "/restrictions", "/companyname1"
  ];

  if (excludeMenuFooterRoutes.includes(location.pathname)) {
    return <>{children}</>;
  }

  return (
    <>
      <Menu />
      {children}
      <Footer />
    </>
  );
};

// Loading component
const LoadingFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    // Optimize font loading
    optimizeFontLoading();
  }, []);

  return (
    <Router>
      <TrackPageViews /> {/* Google Analytics Tracking */}
      <ScrollToTop /> {/* Handle scroll to top */}
      <Layout>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/bookkeeping" element={<Bookkeeping />} />
          <Route path="/cfo-advisory" element={<CFO />} />
          <Route path="/zoho-advisory" element={<Zoho />} />
          <Route path="/tax-return" element={<Tax />} />
          <Route path="/usa-company-formation" element={<USA />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/why-bookscomply" element={<WhyBooksComply />} />
          <Route path="/help" element={<Help />} />
          <Route path="/submitRequest" element={<SubmitRequest />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/subscription-agreement" element={<SubscriptionAgreement />} />
          <Route path="/events/zoho-books-ahmedabad" element={<ZohoEvent />} />

          {/* Help Pages */}
          <Route path="/getting-started" element={<GettingStarted />} />
          <Route path="/how-the-usa-incorporation-process-word" element={<General1 />} />
          <Route path="/can-a-non-us-resident-or-non-us-citizen-incorporate-in-the-us" element={<General2 />} />
          <Route path="/why-should-your-startup-incorporate-in-the-usa" element={<General3 />} />
          <Route path="/ein-employer-identification-number-what-it-is" element={<General4 />} />
          <Route path="/what-are-the-ongoing-compliance-requirements-after-us-incorporation?" element={<General5 />} />
          <Route path="/opening-a-us-business-bank-account-as-a-non-resident-what-are-your-options?" element={<General6 />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/single-member-vs-multi-member-llc-which-one-is-right-for-you?" element={<CompanyType1 />} />
          <Route path="/llc-vs-c-corporation-which-business-structure-is-right-for-you?" element={<CompanyType2 />} />
          <Route path="/why-choose-a-wyoming-llc?" element={<State2 />} />
          <Route path="/are-there-any-restricted-countries-for-company-incorporation-in-the-usa?" element={<Restrictions />} />
          <Route path="/restricted-business-names-in-the-us-what-you-need-to-know" element={<CompanyName1 />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/what-is-your-refund-policy?" element={<Refund1 />} />
          <Route path="/what-payment-methods-do-you-accept?" element={<Refund2 />} />
          <Route path="/how-long-does-it-take-to-process-a-refund?" element={<Refund3 />} />
          <Route path="/can-i-upgrade-or-change-my-service-plan-after-payment?" element={<Refund4 />} />
          {/* 404 - Not Found Page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </Layout>
    </Router>
  );
}

export default App;